import React, { FC, useState, useCallback } from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import { orderFeedbackEvent } from '../tracking/orderFeedbackTracking';
import { graphql, createFragmentContainer, RelayProp } from 'react-relay';
import { SellerReviewsModalAsync } from 'dibs-seller-reviews-modal/exports/SellerReviewsModalAsync';

// components
import { Link } from 'dibs-elements/exports/Link';

// types
import { PdpSharedDealerDetailsFeedback_seller as Seller } from './__generated__/PdpSharedDealerDetailsFeedback_seller.graphql';

// styles
import styles from './PdpSharedDealerDetailsFeedback.scss';

type Props = {
    seller: Seller;
    relay: RelayProp;
};

const PdpSharedDealerDetailsFeedbackComponent: FC<Props> = ({ seller, relay }) => {
    const { orderFeedbackPdp: reviews, serviceId } = seller || {};
    const numberOfReviews = reviews?.totalResults;
    const [isReviewsModalOpen, setIsReviewModalOpen] = useState(false);

    const openModal = useCallback(() => {
        orderFeedbackEvent({
            action: 'seller reviews expanded',
            label: 'pdp',
        });
        setIsReviewModalOpen(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setIsReviewModalOpen(false);
    }, []);

    if (!numberOfReviews) {
        return null;
    }

    return (
        <>
            <Link
                onClick={openModal}
                className={styles.reviewsLink}
                dataTn="order-feedback-modal-trigger"
            >
                <FormattedMessage
                    id="PdpSharedDealerDetailsFeedback.seeReviews"
                    defaultMessage="See {numberOfReviews} {review}"
                    values={{
                        numberOfReviews,
                        review: (
                            <FormattedPlural value={numberOfReviews} one="Review" other="Reviews" />
                        ),
                    }}
                />
            </Link>
            <SellerReviewsModalAsync
                environment={relay.environment}
                isOpen={isReviewsModalOpen}
                onClose={onCloseModal}
                sellerId={serviceId}
            />
        </>
    );
};

export const PdpSharedDealerDetailsFeedback = createFragmentContainer(
    PdpSharedDealerDetailsFeedbackComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDetailsFeedback_seller on Seller {
                serviceId
                orderFeedbackPdp {
                    totalResults
                    edges {
                        node {
                            serviceId
                        }
                    }
                }
            }
        `,
    }
);
